import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouterState } from 'connected-react-router';

import { BoardStatus, boardState, setStatus } from 'common/modules/app/board';
import { Master } from 'common/modules/app/master';
import { GlossaryState } from 'common/modules/app/glossaries';
import { Glossary } from 'common/utils/glossary';
import {
  UserInformation,
  updateUser,
  UpdateKeyValue
} from 'common/modules/domain/user';
import { saveCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { AppState } from 'pages';
import { SelectCountryBoard } from 'pages/board/components/SelectCountryBoard';
import { FetchState } from 'common/modules/app/fetch';
import { GET_PLAN_LIST_ID } from 'common/modules/domain/plan';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const masterState = useSelector<AppState, Master>(
    (state) => state.app.master
  );
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const fetchProcess = fetchState[GET_PLAN_LIST_ID];

  return {
    board: boardState,
    master: masterState,
    user: userState,
    glossary: Glossary.instance(glState),
    router: routerState,
    fetchProcess: fetchProcess,
    clickStatus: boardState.clickStatus
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _board = state.board;
  const _master = state.master;
  const _user = state.user;
  const _searchParams = new URLSearchParams(state.router.location.search);
  const _serviceId = _searchParams.get('serviceId') || '';

  /** SelectCountryBoardの表示有無 */
  const _isDisplay = _board.status === BoardStatus.COUNTRY_DISP;
  /** クローズボタンをクリックした際の処理 */
  const onClickClose = () => {
    dispatch(setStatus(BoardStatus.NOT_DISP));
  };
  /** 地域を選択した際の処理 */
  const onClickRegion = (id: string) => {
    // 選択した地域をuserStateに設定
    dispatch(updateUser(UpdateKeyValue.KEY_REGION, id));
  };
  /** 地域リストを作成 */
  const _regionList = [];
  for (const [key, value] of Object.entries(_master.country)) {
    const _select = _user.region === key ? true : false;
    _regionList.push({
      id: key,
      text: state.glossary.glx(value.glossary),
      select: _select,
      onClick: onClickRegion
    });
  }
  /** 国を選択した際の処理 */
  const onClickCountry = (id: string) => {
    // 選択した国をCookieに設定
    saveCookie(SasCookieKeys.KEY_REGION, id, 180);
    // SelectCountryBoardを非表示
    dispatch(setStatus(BoardStatus.NOT_DISP));
    // 元々表示していた画面を再表示
    window.location.reload();
  };

  /** 国リストを作成 */
  const _countryList = [];
  // 取得したマスター情報の中に選択された地域の国情報があるか確認
  if (_master.country[_user.region]) {
    const _countries = _master.country[_user.region].countries;
    for (const country of _countries) {
      const _select = _user.country === country.code ? true : false;
      _countryList.push({
        id: country.code,
        text: state.glossary.glx(country.glossary),
        select: _select,
        onClick: onClickCountry
      });
    }
  }

  return {
    isDisplay: _isDisplay,
    glossary: state.glossary,
    onClickClose: onClickClose,
    regions: _regionList,
    selectedRegion: _user.region,
    countries: _countryList,
    selectedCountry: _user.country
  };
};

/**
 * 国地域選択画面全体のContainer Component
 */
export default function ContainerComponent(ownProps: { isMobile?: boolean }) {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  return <SelectCountryBoard {...{ ..._props, ...ownProps }} />;
}
